import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import type { Certification, Education, ParsedResume, WorkExperience } from "@epicredible/shared";
import { ApiError, api, type Resume } from "../lib/api.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Input, Textarea } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Badge } from "../components/ui/badge.js";

/** Empty defaults for freshly-added list rows. */
const EMPTY_WORK: WorkExperience = {
  company: "",
  title: "",
  startDate: null,
  endDate: null,
  current: false,
  description: null,
  highlights: [],
};
const EMPTY_EDU: Education = { institution: "", degree: null, field: null, startDate: null, endDate: null };
const EMPTY_CERT: Certification = { name: "", issuer: null, issueDate: null, credentialId: null, url: null };

const EMPTY_PARSED: ParsedResume = {
  contact: { name: null, email: null, phone: null, location: null },
  summary: null,
  work: [],
  education: [],
  skills: [],
  certifications: [],
};

/** Turn "" into null so nullable/validated fields (email, url, dates) pass Zod. */
function nn(v: string): string | null {
  const t = v.trim();
  return t === "" ? null : t;
}

/** Manual review + edit of a resume's extracted data (REQ-2.3). */
export function ResumeEditPage() {
  const { id = "" } = useParams();
  const [resume, setResume] = useState<Resume | null>(null);
  const [draft, setDraft] = useState<ParsedResume | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function load() {
    void api.getResume(id).then((r) => {
      setResume(r.resume);
      setDraft(r.resume.parsed ?? EMPTY_PARSED);
    });
  }
  useEffect(load, [id]);

  async function reparse() {
    setError(null);
    setBusy(true);
    try {
      const r = await api.reparseResume(id);
      setResume(r.resume);
      setDraft(r.resume.parsed ?? EMPTY_PARSED);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Re-parse failed");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!draft) return;
    setError(null);
    setSaved(false);
    setBusy(true);
    // Coerce empty strings on nullable fields to null before validation.
    const payload: ParsedResume = {
      contact: {
        name: nn(draft.contact.name ?? ""),
        email: nn(draft.contact.email ?? ""),
        phone: nn(draft.contact.phone ?? ""),
        location: nn(draft.contact.location ?? ""),
      },
      summary: nn(draft.summary ?? ""),
      skills: draft.skills.map((s) => s.trim()).filter(Boolean),
      work: draft.work.map((w) => ({
        company: w.company.trim(),
        title: w.title.trim(),
        startDate: nn(w.startDate ?? ""),
        endDate: nn(w.endDate ?? ""),
        current: w.current,
        description: nn(w.description ?? ""),
        highlights: w.highlights.map((h) => h.trim()).filter(Boolean),
      })),
      education: draft.education.map((e) => ({
        institution: e.institution.trim(),
        degree: nn(e.degree ?? ""),
        field: nn(e.field ?? ""),
        startDate: nn(e.startDate ?? ""),
        endDate: nn(e.endDate ?? ""),
      })),
      certifications: draft.certifications.map((c) => ({
        name: c.name.trim(),
        issuer: nn(c.issuer ?? ""),
        issueDate: nn(c.issueDate ?? ""),
        credentialId: nn(c.credentialId ?? ""),
        url: nn(c.url ?? ""),
      })),
    };
    try {
      const r = await api.updateResume(id, payload);
      setResume(r.resume);
      setDraft(r.resume.parsed ?? payload);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!resume || !draft) {
    return <div className="h-64 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />;
  }

  const setContact = (patch: Partial<ParsedResume["contact"]>) =>
    setDraft({ ...draft, contact: { ...draft.contact, ...patch } });
  const setWork = (i: number, patch: Partial<WorkExperience>) =>
    setDraft({ ...draft, work: draft.work.map((w, j) => (j === i ? { ...w, ...patch } : w)) });
  const setEdu = (i: number, patch: Partial<Education>) =>
    setDraft({ ...draft, education: draft.education.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  const setCert = (i: number, patch: Partial<Certification>) =>
    setDraft({ ...draft, certifications: draft.certifications.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/resumes">
            <ArrowLeft size={16} /> Back
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{resume.fileName ?? "Resume"}</h1>
        <Badge variant={resume.status === "failed" ? "revoked" : "neutral"}>{resume.status}</Badge>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => void reparse()} disabled={busy}>
            <RefreshCw size={14} /> Re-parse
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={busy}>
            <Save size={14} /> {saved ? "Saved" : "Save"}
          </Button>
        </div>
      </div>

      {resume.status === "failed" && resume.error && (
        <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          Extraction failed: {resume.error}
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Contact + summary */}
      <Card>
        <CardHeader>
          <CardTitle>Contact</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" value={draft.contact.name ?? ""} onChange={(v) => setContact({ name: v })} />
          <Field label="Email" value={draft.contact.email ?? ""} onChange={(v) => setContact({ email: v })} />
          <Field label="Phone" value={draft.contact.phone ?? ""} onChange={(v) => setContact({ phone: v })} />
          <Field label="Location" value={draft.contact.location ?? ""} onChange={(v) => setContact({ location: v })} />
          <div className="sm:col-span-2">
            <Label>Summary</Label>
            <Textarea
              rows={3}
              value={draft.summary ?? ""}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card>
        <CardHeader>
          <CardTitle>Skills</CardTitle>
        </CardHeader>
        <CardContent>
          <Label>Comma-separated</Label>
          <Textarea
            rows={2}
            value={draft.skills.join(", ")}
            onChange={(e) => setDraft({ ...draft, skills: e.target.value.split(",") })}
          />
        </CardContent>
      </Card>

      {/* Work experience */}
      <SectionList
        title="Work experience"
        onAdd={() => setDraft({ ...draft, work: [...draft.work, { ...EMPTY_WORK }] })}
      >
        {draft.work.map((w, i) => (
          <ListRow key={i} onRemove={() => setDraft({ ...draft, work: draft.work.filter((_, j) => j !== i) })}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Company" value={w.company} onChange={(v) => setWork(i, { company: v })} />
              <Field label="Title" value={w.title} onChange={(v) => setWork(i, { title: v })} />
              <Field label="Start" value={w.startDate ?? ""} onChange={(v) => setWork(i, { startDate: v })} />
              <Field label="End" value={w.endDate ?? ""} onChange={(v) => setWork(i, { endDate: v })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={w.current} onChange={(e) => setWork(i, { current: e.target.checked })} />
              Current role
            </label>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={w.description ?? ""} onChange={(e) => setWork(i, { description: e.target.value })} />
            </div>
            <div>
              <Label>Highlights (one per line)</Label>
              <Textarea
                rows={3}
                value={w.highlights.join("\n")}
                onChange={(e) => setWork(i, { highlights: e.target.value.split("\n") })}
              />
            </div>
          </ListRow>
        ))}
      </SectionList>

      {/* Education */}
      <SectionList
        title="Education"
        onAdd={() => setDraft({ ...draft, education: [...draft.education, { ...EMPTY_EDU }] })}
      >
        {draft.education.map((e, i) => (
          <ListRow key={i} onRemove={() => setDraft({ ...draft, education: draft.education.filter((_, j) => j !== i) })}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Institution" value={e.institution} onChange={(v) => setEdu(i, { institution: v })} />
              <Field label="Degree" value={e.degree ?? ""} onChange={(v) => setEdu(i, { degree: v })} />
              <Field label="Field" value={e.field ?? ""} onChange={(v) => setEdu(i, { field: v })} />
              <Field label="Start" value={e.startDate ?? ""} onChange={(v) => setEdu(i, { startDate: v })} />
              <Field label="End" value={e.endDate ?? ""} onChange={(v) => setEdu(i, { endDate: v })} />
            </div>
          </ListRow>
        ))}
      </SectionList>

      {/* Certifications */}
      <SectionList
        title="Certifications"
        onAdd={() => setDraft({ ...draft, certifications: [...draft.certifications, { ...EMPTY_CERT }] })}
      >
        {draft.certifications.map((c, i) => (
          <ListRow
            key={i}
            onRemove={() => setDraft({ ...draft, certifications: draft.certifications.filter((_, j) => j !== i) })}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" value={c.name} onChange={(v) => setCert(i, { name: v })} />
              <Field label="Issuer" value={c.issuer ?? ""} onChange={(v) => setCert(i, { issuer: v })} />
              <Field label="Issue date" value={c.issueDate ?? ""} onChange={(v) => setCert(i, { issueDate: v })} />
              <Field label="Credential ID" value={c.credentialId ?? ""} onChange={(v) => setCert(i, { credentialId: v })} />
              <Field label="URL" value={c.url ?? ""} onChange={(v) => setCert(i, { url: v })} />
            </div>
          </ListRow>
        ))}
      </SectionList>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SectionList({ title, onAdd, children }: { title: string; onAdd: () => void; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        <Button size="sm" variant="secondary" onClick={onAdd}>
          <Plus size={14} /> Add
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function ListRow({ onRemove, children }: { onRemove: () => void; children: React.ReactNode }) {
  return (
    <div className="space-y-3 rounded-md border border-border p-4">
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={onRemove}>
          <Trash2 size={14} /> Remove
        </Button>
      </div>
      {children}
    </div>
  );
}
